/* -------------------------------------------------------------------------- */
/* --------------------------- THEME : FRAMEWORKS --------------------------- */

// FRAMEWORKS : NODES_MODULES
import gsap from "gsap";
import "gsap/EasePack";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import ScrollSmoother from "gsap/ScrollSmoother";
import DrawSVGPlugin from "gsap/DrawSVGPlugin";
gsap.registerPlugin(ScrollSmoother, ScrollTrigger, SplitText, DrawSVGPlugin);

import Swiper from "swiper";
import {
	Autoplay,
	FreeMode,
	// Thumbs,
	// EffectFade,
	// Navigation,
	// Pagination,
	Scrollbar,
} from "swiper/modules";

import "leaflet";

/* -------------------------------------------------------------------------- */
/* ------------------------ THEME : GLOBAL VARIABLES ------------------------ */

// Default : Smooth scrolling
let scrollSmooth = false;

/* -------------------------------------------------------------------------- */
/* ------------------------- THEME : GLOBAL EVENTS -------------------------- */

// DOCUMENT READY :
document.addEventListener("DOMContentLoaded", () => {
	// remove 'no-js' body class
	document.body.classList.remove("page-no-js");
	gsapPreloader();
	scrollDetection(160);
	menuBurger();
	menuDropdown();
	bannerInit();
	bannerToMarquee();
	dataPopin();
	dataToggle();
	dataAccordion();
	dataSrc();
	loanSimulator();
	swiperInit();
	leafletDataMap();
});

// DOCUMENT RESIZED (avec ScrollTrigger)
ScrollTrigger.addEventListener("refreshInit", () => {
	gsapScrollSmootherRefresh();
	bannerToMarquee();
});

/* -------------------------------------------------------------------------- */
/* ----------------------- THEME : GLOBAL FUNCTIONS ------------------------- */

// Activation/désactivation du scroll
const scrollEnabled = (bool) => {
	if (bool === false || bool === true) {
		const enableScroll = bool === true;

		if (scrollSmooth) {
			scrollSmooth.paused(!enableScroll);
		}

		document.documentElement.style.overflowY = enableScroll ? "auto" : "hidden";
	}
};

// Scroll vers une ancre
const scrollToAnchor = (scrollOffset = 100) => {
	// Fonction pour gérer le défilement
	const scrollToElement = (target) => {
		const element = document.querySelector(target);
		if (element) {
			if (scrollSmooth) {
				// Avec ScrollSmoother
				scrollSmooth.scrollTo(target, true, `top ${scrollOffset}px`);
			} else {
				// Par défaut
				window.scrollTo({
					top:
						element.getBoundingClientRect().top + window.scrollY - scrollOffset,
					behavior: "smooth",
				});
			}
		}
	};

	// au chargement
	const hashInUrl = window.location.hash;
	if (hashInUrl) {
		scrollToElement(hashInUrl);
	}

	// avec un [href="#..."]
	const linksWithAnchor = document.querySelectorAll('a[href^="#"]');
	linksWithAnchor.forEach((link) => {
		link.addEventListener("click", (event) => {
			const anchorHref = link.getAttribute("href");
			if (anchorHref !== "#") {
				event.preventDefault();
				scrollToElement(anchorHref);
			}
		});
	});

	// avec un [data-scrollto]
	const dataWithScrollTo = document.querySelectorAll("[data-scrollto]");
	dataWithScrollTo.forEach((data) => {
		data.addEventListener("click", () => {
			let anchorData = data.getAttribute("data-scrollto");
			if (anchorData === "auto") {
				// Calculer la position de défilement pour 'auto'
				const autoOffset = window.innerHeight + window.scrollY - scrollOffset;
				window.scrollTo({
					top: autoOffset,
					behavior: "smooth",
				});
			} else {
				// Utiliser la valeur de data-scrollto comme cible pour scrollToElement
				scrollToElement(anchorData);
			}
		});
	});
};

// Détection du scroll sur le body
const scrollDetection = (scrollOffset = 1) => {
	let position = window.scrollY;

	if (position > scrollOffset) {
		document.body.classList.add("page-scrolled");
	} else {
		document.body.classList.remove("page-scrolled");
	}

	document.addEventListener("scroll", () => {
		// Détecter la direction du scroll
		let scroll = window.scrollY;
		// Scroll vers le bas
		if (scroll > position) {
			if (scroll < scrollOffset) {
				document.body.classList.remove("page-scrolled");
				document.body.classList.remove("page-scrolling");
			} else {
				document.body.classList.add("page-scrolling");
				document.body.classList.add("page-scrolled");
			}
		}
		// Scroll vers le haut
		else {
			document.body.classList.remove("page-scrolling");
			if (scroll < scrollOffset) {
				document.body.classList.remove("page-scrolled");
			}
		}
		// Scroll max ?
		if (scroll + window.innerHeight >= document.documentElement.scrollHeight) {
			document.body.classList.add("page-scroll-completed");
		} else {
			document.body.classList.remove("page-scroll-completed");
		}
		position = scroll;
	});
};

// Gestion du menu burger
const menuBurger = () => {
	const headerBurger = document.querySelector("#page-header-burger");
	const headerBackdrop = document.querySelector("#page-header-backdrop");

	if (headerBurger) {
		headerBurger.addEventListener("click", () => {
			if (document.body.classList.contains("page-with-fullscreenmenu")) {
				scrollEnabled(true);
				document.body.classList.remove("page-with-fullscreenmenu");
			} else {
				scrollEnabled(false);
				document.body.classList.add("page-with-fullscreenmenu");
				gsapBurgerAnimation();
			}
		});
	}

	if (headerBackdrop) {
		headerBackdrop.addEventListener("click", () => {
			if (document.body.classList.contains("page-with-fullscreenmenu")) {
				scrollEnabled(true);
				document.body.classList.remove("page-with-fullscreenmenu");
			}
		});
	}
};

// Gestion des dropdowns
const menuDropdown = () => {
	const menuItemLinks = document.querySelectorAll(
		'.menu .menu__item .menu__link[href="#"]'
	);

	menuItemLinks.forEach((item) => {
		item.closest(".menu__item").classList.add("menu__item--parent");
		item.addEventListener("click", (event) => {
			event.preventDefault();
			item.closest(".menu__item").classList.toggle("menu__item--active");
		});
	});

	document.addEventListener("mouseup", (event) => {
		const activeMenuItems = document.querySelectorAll(
			".menu .menu__item.menu__item--active"
		);

		activeMenuItems.forEach((activeItem) => {
			if (
				!activeItem.isEqualNode(event.target) &&
				!activeItem.contains(event.target)
			) {
				activeItem.classList.remove("menu__item--active");
			}
		});
	});
};

// Activation/désactivation de la bannière
const bannerInit = () => {
	// Vérifie si la bannière a déjà été fermée durant cette session
	if (sessionStorage.getItem("bannerClosed") === "true") {
		return;
	}

	const pageBanner = document.querySelector("#page-banner");
	const pageBannerClose = document.querySelector("#page-banner-close");

	if (pageBanner) {
		// actualisation
		document.body.classList.add("page-with-banner");
		// bouton de fermeture
		pageBannerClose.addEventListener("click", () => {
			document.body.classList.remove("page-with-banner");

			// Enregistre en session que la bannière a été fermée
			sessionStorage.setItem("bannerClosed", "true");
		});
	}
};

// Gestion du défilement horizontal de la bannière
const bannerToMarquee = () => {
	const pageBanner = document.querySelector("#page-banner");

	if (pageBanner) {
		const bannerwrapperWidth = pageBanner.offsetWidth;
		const bannerTextWidth =
			pageBanner.querySelector(".banner__text").offsetWidth;
		if (bannerwrapperWidth > bannerTextWidth + 60) {
			pageBanner.classList.remove("page__banner--scrolling");
		} else {
			pageBanner.classList.add("page__banner--scrolling");
		}
	}
};

// Gestions des actions des [data-toggle]
const dataToggle = () => {
	// Récupération de tous les écouteurs
	const toggleListeners = document.querySelectorAll("[data-toggle]");

	// Écouteur d'événement pour chaque écouteur
	toggleListeners.forEach((togglelistener) => {
		const toggleTargetSelector = togglelistener.dataset.toggle;
		const toggleTarget = toggleTargetSelector
			? document.querySelector(toggleTargetSelector)
			: null;

		togglelistener.addEventListener("click", (event) => {
			if (toggleTarget) {
				// event.stopPropagation();
				toggleTarget.classList.toggle("--active");
			}
		});
	});

	// Gestionnaire pour les clics hors des éléments toggle
	document.addEventListener("click", (event) => {
		toggleListeners.forEach((togglelistener) => {
			const toggleTargetSelector = togglelistener.dataset.toggle;
			const toggleTarget = toggleTargetSelector
				? document.querySelector(toggleTargetSelector)
				: null;
			if (
				toggleTarget &&
				toggleTarget.classList.contains("--active") &&
				!toggleTarget.contains(event.target) &&
				!togglelistener.contains(event.target)
			) {
				toggleTarget.classList.remove("--active");
			}
		});
	});
};

// Gestions des actions des [data-accordion]
const dataAccordion = () => {
	const accordionListeners = document.querySelectorAll("[data-accordion]");

	accordionListeners.forEach((accordionElement) => {
		accordionElement.addEventListener("click", (event) => {
			event.stopPropagation();

			// Retirer la classe "--active" à tous les accordionListeners et accordionTargets
			accordionListeners.forEach((element) => {
				if (element !== accordionElement) {
					const accordionTarget = document.getElementById(
						element.dataset.accordion
					);
					if (accordionTarget) {
						element.classList.remove("--active");
						accordionTarget.classList.remove("--active");
					}
				}
			});

			// Ajouter la classe "--active" à la cible
			if (accordionElement.dataset.accordion) {
				const currentaccordionTarget = document.getElementById(
					accordionElement.dataset.accordion
				);
				if (currentaccordionTarget) {
					accordionElement.classList.add("--active");
					currentaccordionTarget.classList.add("--active");
				}
			}
		});
	});
};

// Gestions des actions des [data-popin]
const dataPopin = () => {
	const popins = document.querySelectorAll("[data-popin]");

	popins.forEach((popin) => {
		popin.addEventListener("click", (event) => {
			// event.preventDefault();

			// ouvrir/fermer la popin
			const thispopinName = popin.dataset.popin;
			if (thispopinName) {
				const thispopinEl = document.querySelector(
					`.page__popin#${thispopinName}`
				);
				thispopinEl.classList.toggle("page__popin--open");
			}

			// mettre les vidéos des popins en pause
			const htmlVideoFrompopin =
				document.querySelectorAll(".page__popin video");
			if (htmlVideoFrompopin.length > 0) {
				htmlVideoFrompopin.forEach((video) => {
					video.pause();
				});
			}
		});
	});
};

// Convertir les [data-src] en [src]
const dataSrc = () => {
	// Select all elements with a data-src attribute
	const srcElements = document.querySelectorAll("[data-src]");
	srcElements.forEach((element) => {
		element.setAttribute("src", element.getAttribute("data-src"));
		element.removeAttribute("data-src");
	});
};

// Simulateur de crédit
const loanSimulator = () => {
	// Simulateur de crédit - intégration avec taux dynamiques
	const simulatorForm = document.getElementById("simulator-form");

	if (simulatorForm) {
		// Taux de référence basé sur data-rate-25 ou un fallback de 4.0 par défaut
		const defaultRate =
			parseFloat(simulatorForm.getAttribute("data-rate-25")) || 4.0;

		// Fonction pour récupérer le taux en fonction de la durée
		function getRate(duration) {
			// Commence avec le taux de référence (data-rate-25 ou taux par défaut)
			let rate = defaultRate;

			// Vérifie les autres data-rate-* et remplace si applicable
			const rateMap = {
				20: parseFloat(simulatorForm.getAttribute("data-rate-20")),
				15: parseFloat(simulatorForm.getAttribute("data-rate-15")),
				10: parseFloat(simulatorForm.getAttribute("data-rate-10")),
				5: parseFloat(simulatorForm.getAttribute("data-rate-5")),
			};

			// Parcourt les taux dans l'ordre décroissant
			for (const [maxDuration, specificRate] of Object.entries(rateMap).sort(
				(a, b) => b[0] - a[0]
			)) {
				if (duration <= maxDuration && specificRate) {
					rate = specificRate;
				}
			}

			return rate;
		}

		// Fonction principale de simulation de crédit
		function simulate() {
			// Récupération des valeurs saisies
			const durationInput = document.getElementById("duration");
			const amountInput = document.getElementById("amount");

			const duration = parseFloat(durationInput?.value || 0);
			const value = parseFloat(amountInput?.value || 0);

			if (!isNaN(duration) && !isNaN(value)) {
				// Récupération du taux en fonction de la durée
				let rate = getRate(duration);

				// Calcul de la mensualité :
				// m = [C * t/12] / [1 − (1 + t/12)^(-n)]
				let monthlyRate = rate / 100 / 12;
				let numPayments = duration * 12;
				let output = Math.floor(
					(value * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -numPayments))
				);

				// Vérification si output est NaN ou Infinity
				if (isNaN(output) || !isFinite(output)) {
					output = 0;
				}

				// Mise à jour de l'affichage du résultat
				const resultElement = document.getElementById("simulator-callback");
				if (resultElement) {
					// console.log("Taux utilisé: ", rate);
					resultElement.textContent = output; // Affiche la mensualité calculée
				}
			}
		}

		// Écoute des changements sur les champs de saisie
		const durationInput = document.getElementById("duration");
		const amountInput = document.getElementById("amount");

		if (durationInput && amountInput) {
			durationInput.addEventListener("input", simulate); // Sur modification de la durée
			amountInput.addEventListener("input", simulate); // Sur modification du montant
		}

		// Lancer une simulation initiale
		simulate();
	}
};

/* -------------------------------- (SWIPER) -------------------------------- */

// Swiper : Initialisation des sliders
const swiperInit = () => {
	// .swiper[data-swiper='scrollbar']
	const scrollbar = document.querySelectorAll(
		".swiper[data-swiper='scrollbar']"
	);
	scrollbar.forEach((swiper) => {
		const slides = swiper.querySelectorAll(".swiper-wrapper .swiper-slide");
		if (slides.length > 1) {
			new Swiper(swiper, {
				// Core parameters
				slidesPerView: "auto",
				speed: 1200,
				resizeObserver: true,
				// Modules
				modules: [Autoplay, Scrollbar],
				autoplay: {
					delay: 2400,
					disableOnInteraction: true,
				},
				scrollbar: {
					el: swiper.querySelector(".swiper-scrollbar"),
					draggable: true,
					hide: true,
				},
			});
		}
	});

	// .swiper[data-swiper='autoleft']
	const autoleft = document.querySelectorAll(".swiper[data-swiper='autoleft']");
	autoleft.forEach((swiper) => {
		const slides = swiper.querySelectorAll(".swiper-wrapper .swiper-slide");
		if (slides.length > 1) {
			new Swiper(swiper, {
				// Core parameters
				slidesPerView: "auto",
				speed: slides.length * 3000,
				loop: true,
				resizeObserver: true,
				// Modules
				modules: [FreeMode, Autoplay],
				freeMode: true,
				autoplay: {
					delay: 0,
					disableOnInteraction: false,
				},
			});
		}
	});

	// .swiper[data-swiper='autoright']
	const autoright = document.querySelectorAll(
		".swiper[data-swiper='autoright']"
	);
	autoright.forEach((swiper) => {
		const slides = swiper.querySelectorAll(".swiper-wrapper .swiper-slide");
		if (slides.length > 1) {
			new Swiper(swiper, {
				// Core parameters
				slidesPerView: "auto",
				speed: slides.length * 3000,
				loop: true,
				resizeObserver: true,
				// Modules
				modules: [FreeMode, Autoplay],
				freeMode: true,
				autoplay: {
					delay: 0,
					disableOnInteraction: false,
					reverseDirection: true,
				},
			});
		}
	});
};

/* -------------------------------- (LEAFLET) ------------------------------- */

// LEAFLET : Initialisation des maps
const leafletDataMap = () => {
	const allDataMap = document.querySelectorAll("[data-map]");
	allDataMap.forEach((dataMap) => {
		// Récupération de l'adresse à partir de l'attribut [data-map]
		const address = dataMap.getAttribute("data-map");

		// Appel à l'API Nominatim pour obtenir les coordonnées à partir de l'adresse
		async function fetchCoordinates(address) {
			const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
				address
			)}&format=json&limit=1`;
			const response = await fetch(url);
			const data = await response.json();
			if (data.length > 0) {
				return {
					lat: data[0].lat,
					lon: data[0].lon,
				};
			} else {
				throw new Error("Adresse introuvable");
			}
		}

		// Initialisation de la carte et ajout du cercle
		async function initializeMap(address) {
			try {
				const coordinates = await fetchCoordinates(address);
				const lat = coordinates.lat;
				const lon = coordinates.lon;

				// Initialisation de la map centrée sur les coordonnées récupérées
				const leafletMap = L.map(dataMap, {
					scrollWheelZoom: false, // Désactive le zoom à la molette
				}).setView([lat, lon], 12);

				// Ajout de la couche de tuiles OpenStreetMap
				L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
					minZoom: 6,
					maxZoom: 15,
				}).addTo(leafletMap);

				// Création d'un cercle aux coordonnées de l'adresse récupérée
				const circle = L.circle([lat, lon], {
					color: "var(--color-primary)",
					fillColor: "var(--color-primary)",
					fillOpacity: 0.5,
					radius: 3000, // Rayon du cercle en mètres
				}).addTo(leafletMap);

				// circle.bindPopup(`${address}`).openPopup();
			} catch (error) {
				console.error(
					`Erreur lors de la récupération des coordonnées : ${error.message}`
				);
			}
		}

		// Exécution de la fonction pour chaque carte
		initializeMap(address);
	});
};

/* --------------------------------- (GSAP) --------------------------------- */

// GSAP : Initialisation du scrollSmoother (avec ScrollSmoother)
const initScrollSmooth = () => {
	scrollSmooth = ScrollSmoother.create({
		wrapper: "#page-wrapper",
		content: "#page-container",
		smooth: 1,
		smoothTouch: false,
		effects: true,
		normalizeScroll: false,
		ignoreMobileResize: true,
	});
};

// GSAP : Animation d'apparition du menu fullscreen
const gsapBurgerAnimation = () => {
	const burgerTimeline = gsap.timeline();
	burgerTimeline
		.from("#page-header .header__fullscreenmenu__menu", {
			delay: 0.3,
			duration: 0.6,
			ease: "power1.out",
			opacity: 0,
			y: 40,
		})
		.from(
			"#page-header .header__fullscreenmenu .menu__item span",
			{
				delay: 0.1,
				duration: 0.6,
				stagger: 0.05,
				ease: "power1.out",
				opacity: 0,
				y: 40,
			},
			"<"
		)
		.from(
			"#page-header .header__fullscreenmenu__brand svg",
			{
				delay: 0.1,
				duration: 0.6,
				ease: "power1.out",
				opacity: 0,
				y: 40,
			},
			"<"
		)
		.from(
			"#page-header .header__fullscreenmenu__contact .contact__item",
			{
				delay: 0.1,
				duration: 0.6,
				stagger: 0.05,
				ease: "power1.out",
				opacity: 0,
				y: 40,
			},
			"<"
		)
		.from(
			"#page-header .header__fullscreenmenu__address .address__item",
			{
				delay: 0.1,
				duration: 0.6,
				stagger: 0.05,
				ease: "power1.out",
				opacity: 0,
				y: 40,
			},
			"<"
		)
		.from(
			"#page-header .header__fullscreenmenu__social .social__item",
			{
				delay: 0.1,
				duration: 0.6,
				stagger: 0.05,
				ease: "power1.out",
				opacity: 0,
				y: 40,
			},
			"<"
		);
};

// GSAP : Découpage des textes (avec SplitText)
const gsapSplitText = () => {
	const textsToAnimated = document.querySelectorAll([
		// global
		'[data-animation^="reveal-text"]',
		".elementor-heading-title",
	]);

	textsToAnimated.forEach((_thisText) => {
		new SplitText(_thisText, {
			tag: "span",
			// type: "lines, words, chars",
			type: "lines, words",
			linesClass: "line",
			wordsClass: "word",
			charsClass: "char",
		});
	});
};

// GSAP : Animations au scroll (avec ScrollTrigger)
const gsapDataAnimation = () => {
	// [data-animation] + .elementor-element
	const elementsToAnimated = document.querySelectorAll([
		"[data-animation]",
		".elementor-heading-title",
		".elementor-element > .elementor-element > .elementor-element:not([data-animation])",
	]);
	elementsToAnimated.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10)
			: 0;
		ScrollTrigger.create({
			trigger: _this,
			start: "top 100%",
			end: "bottom 0",
			onEnter: () => {
				setTimeout(() => {
					_this.classList.add("--animated");
				}, delay);
			},
			onLeaveBack: () => {
				_this.classList.remove("--animated");
			},
			// markers: true,
		});
	});

	// data-animation="counter"
	const elementsToCounter = document.querySelectorAll(
		'[data-animation="counter"]'
	);
	elementsToCounter.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10)
			: 0;
		ScrollTrigger.create({
			trigger: _this,
			start: "top 100%",
			end: "bottom 0%",
			// markers: true,
			onEnter: () => {
				setTimeout(() => {
					gsap.from(_this, {
						duration: 1.5,
						textContent: 0,
						snap: { textContent: 1 },
						stagger: {
							each: 1.0,
							onUpdate: () => {
								this.targets()[0].innerHTML = Math.ceil(
									this.targets()[0].textContent
								)
									// conserver les "."
									.toString()
									.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
									// conserver les espaces
									.toString()
									.replace(/\B(?=(\d{3})+(?!\d))/g, "&nbsp;");
							},
						},
					});
				}, delay);
			},
		});
	});

	// data-animation="reveal-text-reading"
	const textsToReading = document.querySelectorAll([
		'[data-animation="reveal-text-reading"]',
	]);
	textsToReading.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10)
			: 0;
		gsap.fromTo(
			_this.querySelectorAll(".word"),
			{
				opacity: 0.33,
			},
			{
				delay: delay,
				stagger: 0.3,
				ease: "power1.out",
				opacity: 1,
				scrollTrigger: {
					trigger: _this,
					start: "top 88%",
					end: "top 22%",
					scrub: true,
					// markers: true,
				},
			}
		);
	});

	// data-animation="skid"
	const elementsToskid = document.querySelectorAll([
		'body:not(.elementor-editor-active) [data-animation="skid"]',
	]);
	elementsToskid.forEach((_this) => {
		gsap.to(_this, {
			scrollTrigger: {
				trigger: _this,
				pin: true,
				pinSpacing: false,
				start: "top top",
				// markers: true,
			},
		});
	});

	// data-animation="draw"
	const elementsToDraw = document.querySelectorAll([
		'body:not(.elementor-editor-active) [data-animation="draw"]',
	]);
	elementsToDraw.forEach((_this) => {
		gsap.from(_this, {
			duration: 2.4,
			ease: "power3.out",
			drawSVG: "0%",
			scrollTrigger: {
				trigger: _this,
				start: "top top",
				// markers: true,
			},
		});
	});
};

// Actions exécutées après le preLoader d'accueil
const afterPreloader = () => {
	scrollEnabled(true);
	gsapDataAnimation();
	scrollToAnchor();
};

// GSAP : Rafraîchissement du ScrollSmoother (avec ScrollSmoother)
const gsapScrollSmootherRefresh = () => {
	if (scrollSmooth) {
		// Désactiver sur Élémentor
		if (document.querySelector("body.elementor-editor-active")) {
			scrollSmooth.kill();
		} else {
			scrollSmooth.refresh();
		}
	}
};

// GSAP : Gestion du preloader d'accueil
const gsapPreloader = () => {
	// Chargement des fonctions GSAP préalables
	initScrollSmooth();
	scrollEnabled(false);
	gsapSplitText();

	// Lancement de l'animation de chargement
	const loaderTimeline = gsap.timeline();
	if (document.body.classList.contains("page-home")) {
		loaderTimeline
			.set("#page-preloader .preloader__content", {
				opacity: 1,
				visibility: "visible",
			})
			.from('#page-preloader [data-name="s"] path', {
				duration: 1.2,
				ease: "power3.inOut",
				drawSVG: "0%",
			})
			.from(
				'#page-preloader [data-name="c"]',
				{
					delay: 0.9,
					duration: 0.6,
					ease: "power3.out",
					opacity: 0,
					y: "10%",
				},
				"<"
			)
			.to("#page-preloader", {
				delay: 0.6,
				duration: 0.6,
				ease: "power1.inOut",
				opacity: 0,
			})
			.set("#page-preloader", {
				visibility: "hidden",
			});
	} else {
		loaderTimeline
			.set("#page-preloader .preloader__content", {
				opacity: 1,
				visibility: "visible",
			})
			.to("#page-preloader", {
				delay: 0.6,
				duration: 0.6,
				ease: "power1.inOut",
				opacity: 0,
			})
			.set("#page-preloader", {
				visibility: "hidden",
			});
	}

	// Functions succédentes
	loaderTimeline.add(afterPreloader, loaderTimeline.duration() - 0.3);
};
